import type {
  ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, Ref,
  SelectHTMLAttributes, TextareaHTMLAttributes,
} from 'react'
import { useId, useState } from 'react'
import { initials } from '../../lib/format'
import { useConfirm } from '../../lib/confirm'
import './ui.css'

/* ── Button ─────────────────────────────────────────────────────────── */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg'
  block?: boolean
  loading?: boolean
  icon?: ReactNode
  /** Ask before acting. Set this on anything that writes, sends, approves
   * or deletes - a stray click on a phone in a mine pit is cheap to make
   * and expensive to undo. Works for `type="submit"` too: the click is
   * intercepted, and the form is submitted only once the person agrees. */
  confirm?: {
    title: string
    message?: ReactNode
    confirmLabel?: string
    destructive?: boolean
  }
}

export function Button({
  variant = 'primary', size = 'md', block, loading, icon,
  children, className = '', disabled, confirm, onClick, ...rest
}: ButtonProps) {
  const ask = useConfirm()

  async function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    if (!confirm) return onClick?.(e)

    // Stop the default action (including a form submit) while we ask.
    const form = e.currentTarget.form
    const isSubmit = (rest.type ?? 'submit') === 'submit' && !!form
    e.preventDefault()

    const ok = await ask({
      title: confirm.title,
      message: confirm.message,
      confirmLabel: confirm.confirmLabel,
      destructive: confirm.destructive,
    })
    if (!ok) return

    if (isSubmit) {
      // requestSubmit runs validation and fires onSubmit, exactly as the
      // real click would have - unlike form.submit(), which skips both.
      form!.requestSubmit()
      return
    }
    onClick?.(e)
  }

  return (
    <button
      className={`btn btn--${variant} btn--${size}${block ? ' btn--block' : ''} ${className}`}
      disabled={disabled || loading}
      onClick={handleClick}
      {...rest}
    >
      {loading ? <span className="btn__spinner" aria-hidden /> : icon}
      {children}
    </button>
  )
}

/* ── Card ───────────────────────────────────────────────────────────── */

export function Card({
  children, pad, hover, className = '', ...rest
}: { children: ReactNode; pad?: boolean; hover?: boolean; className?: string } &
   React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={`card${pad ? ' card--pad' : ''}${hover ? ' card--hover' : ''} ${className}`}
      {...rest}
    >
      {children}
    </div>
  )
}

export function CardHeader({
  title, sub, action,
}: { title: ReactNode; sub?: ReactNode; action?: ReactNode }) {
  return (
    <div className="card__header">
      <div>
        <div className="card__title">{title}</div>
        {sub && <div className="card__sub">{sub}</div>}
      </div>
      {action}
    </div>
  )
}

export function CardBody({
  children, flush,
}: { children: ReactNode; flush?: boolean }) {
  return <div className={`card__body${flush ? ' card__body--flush' : ''}`}>{children}</div>
}

/* ── Field wrapper ──────────────────────────────────────────────────── */

function Field({
  label, hint, error, htmlFor, children,
}: {
  label?: string; hint?: string; error?: string
  htmlFor?: string; children: ReactNode
}) {
  return (
    <div className="field">
      {label && <label className="field__label" htmlFor={htmlFor}>{label}</label>}
      {children}
      {error ? <span className="field__error">{error}</span>
             : hint && <span className="field__hint">{hint}</span>}
    </div>
  )
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string; hint?: string; error?: string
  /* React 19 passes ref to function components as an ordinary prop, so it just
   * needs declaring here to reach the underlying <input> via the spread. */
  ref?: Ref<HTMLInputElement>
}

export function Input({ label, hint, error, className = '', ...rest }: InputProps) {
  const id = useId()
  return (
    <Field label={label} hint={hint} error={error} htmlFor={rest.id ?? id}>
      <input
        id={rest.id ?? id}
        className={`input${error ? ' input--invalid' : ''} ${className}`}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </Field>
  )
}

/* Password field with a reveal toggle - typing a password on a phone in the
 * field is error-prone, and these users often are. */
export function PasswordInput({ label, hint, error, ...rest }: InputProps) {
  const id = useId()
  const [shown, setShown] = useState(false)
  return (
    <Field label={label} hint={hint} error={error} htmlFor={rest.id ?? id}>
      <div className="input-group">
        <input
          id={rest.id ?? id}
          type={shown ? 'text' : 'password'}
          className={`input${error ? ' input--invalid' : ''}`}
          aria-invalid={error ? true : undefined}
          {...rest}
        />
        <button
          type="button"
          className="input-group__action"
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {shown ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>
    </Field>
  )
}

export function Select({
  label, hint, error, children, className = '', ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label?: string; hint?: string; error?: string }) {
  const id = useId()
  return (
    <Field label={label} hint={hint} error={error} htmlFor={rest.id ?? id}>
      <select id={rest.id ?? id} className={`select ${className}`} {...rest}>
        {children}
      </select>
    </Field>
  )
}

export function Textarea({
  label, hint, error, className = '', ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string; hint?: string; error?: string }) {
  const id = useId()
  return (
    <Field label={label} hint={hint} error={error} htmlFor={rest.id ?? id}>
      <textarea
        id={rest.id ?? id}
        className={`textarea${error ? ' textarea--invalid' : ''} ${className}`}
        {...rest}
      />
    </Field>
  )
}

/* ── Avatar ─────────────────────────────────────────────────────────── */

/** A real photo when one's on file, otherwise the same initials-circle
 * fallback used everywhere in the app. Pass a sizing class (e.g. the
 * existing `usr__avatar`) via `className`, or use `size` for a one-off. */
export function Avatar({
  photoUrl, name, email, size, className = '',
}: { photoUrl?: string; name?: string; email?: string; size?: number; className?: string }) {
  const style = size ? { width: size, height: size, fontSize: Math.max(10, size * 0.4) } : undefined
  if (photoUrl) {
    return <img className={`avatar ${className}`} style={style} src={photoUrl} alt="" />
  }
  return <span className={`avatar avatar--fallback ${className}`} style={style}>{initials(name, email)}</span>
}

/* ── Badge ──────────────────────────────────────────────────────────── */

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'accent'

export function Badge({
  tone = 'neutral', dot, children,
}: { tone?: Tone; dot?: boolean; children: ReactNode }) {
  return (
    <span className={`badge badge--${tone}`}>
      {dot && <span className="badge__dot" aria-hidden />}
      {children}
    </span>
  )
}

/* Maps the many status strings the RTDB uses onto a consistent colour. */
export function StatusBadge({ status }: { status?: string }) {
  const s = (status ?? '').toLowerCase()
  const tone: Tone =
    ['active', 'approved', 'verified', 'resolved', 'complete', 'completed'].includes(s) ? 'success'
    : ['pending', 'submitted', 'under_review', 'in_review', 'draft'].includes(s) ? 'warning'
    : ['rejected', 'suspended', 'expired', 'overdue', 'critical'].includes(s) ? 'danger'
    : ['acknowledged', 'open', 'in_progress'].includes(s) ? 'info'
    : 'neutral'
  return <Badge tone={tone} dot>{(status ?? 'unknown').replace(/_/g, ' ')}</Badge>
}

/* ── Alert ──────────────────────────────────────────────────────────── */

export function Alert({
  tone = 'info', children,
}: { tone?: 'danger' | 'warning' | 'success' | 'info'; children: ReactNode }) {
  return (
    <div className={`alert alert--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      <span className="alert__icon" aria-hidden>
        {tone === 'danger' || tone === 'warning' ? <WarnIcon /> : <InfoIcon />}
      </span>
      <div>{children}</div>
    </div>
  )
}

/* ── States ─────────────────────────────────────────────────────────── */

export function Spinner({ size = 20 }: { size?: number }) {
  return <span className="spinner" style={{ width: size, height: size }} role="status" aria-label="Loading" />
}

export function PageLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="center-page">
      <Spinner size={28} />
      <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>{label}</span>
    </div>
  )
}

export function Skeleton({
  h = 16, w = '100%', style,
}: { h?: number | string; w?: number | string; style?: React.CSSProperties }) {
  return <div className="skeleton" style={{ height: h, width: w, ...style }} />
}

export function EmptyState({
  icon, title, body, action,
}: { icon?: ReactNode; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      {icon && <div className="empty__icon">{icon}</div>}
      <div className="empty__title">{title}</div>
      {body && <p className="empty__body">{body}</p>}
      {action}
    </div>
  )
}

/* ── Inline icons (no icon-font dependency) ─────────────────────────── */

const sw = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

function EyeIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" {...sw}>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}
function EyeOffIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" {...sw}>
      <path d="M9.9 4.2A10.5 10.5 0 0 1 12 4c6.4 0 10 7 10 7a18 18 0 0 1-3.2 4.2M6.2 6.2A18 18 0 0 0 2 11s3.6 7 10 7a10 10 0 0 0 4.2-.9" />
      <path d="m2 2 20 20" />
    </svg>
  )
}
function WarnIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" {...sw}>
      <path d="M12 3 2 20h20L12 3Z" /><path d="M12 10v4" /><path d="M12 17.5h.01" />
    </svg>
  )
}
function InfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" {...sw}>
      <circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" />
    </svg>
  )
}
